import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { MeshBVHBackend } from '@/plugins/spatial/MeshBVHBackend'
import type { NormalizedSpatialArtifact } from '@/plugins/spatial/normalizedSpatialArtifact'
import {
  decodeSpatialArtifact,
  encodeSpatialArtifact,
} from '@/plugins/spatial/spatialArtifactCodec'
import type { SpatialRepresentationBuildResult } from '@/plugins/spatial/spatialBackend'
import { getLoggerManager } from '@/shared/logging'
import {
  beginGSplatResourceAttempt,
  measureGSplatLoadStage,
} from '@/shared/telemetry/gsplatLoadTelemetry'
import type {
  SpatialGroupRangeSnapshot,
  SpatialHitMetadata,
  SpatialMeshMetadataSnapshot,
  SpatialRepresentationDebugOptions,
  SpatialRepresentationDebugSnapshot,
  SpatialRepresentationLoadRequest,
  SpatialRepresentationMetadataSnapshot,
  SpatialRepresentationRole,
  SpatialRepresentationSourceSnapshot,
  SpatialSemanticMetadata,
  SpatialSourceFormat,
  SpatialWorldSource,
} from '@/shared/types/spatial'

const log = getLoggerManager().getLogger('spatial:artifact')

/**
 * 空间表达构建流水线，负责从资源加载到后端构建的完整流程。
 */
export interface SpatialBuildPipelineOptions {
  /** 是否携带跨域 Cookie（用于 CloudFront 签名 Cookie 等场景） */
  withCredentials?: boolean
}

export class SpatialBuildPipeline {
  private readonly loadingManager: THREE.LoadingManager
  private readonly withCredentials: boolean
  private readonly artifactCache = new Map<string, Promise<NormalizedSpatialArtifact>>()

  constructor(loadingManager: THREE.LoadingManager, options?: SpatialBuildPipelineOptions) {
    this.loadingManager = loadingManager
    this.withCredentials = options?.withCredentials ?? false
  }

  dispose(): void {
    this.artifactCache.clear()
  }

  /**
   * 构建指定角色的空间表达。
   *
   * @param role - 表达角色（surface/collision/navigation）
   * @param request - 加载请求，包含资源与调试参数
   * @param signal - 可选的取消信号，触发时抛出 AbortError
   * @returns 构建结果，含后端、格式、包围盒与元数据
   */
  async buildRepresentation(
    role: SpatialRepresentationRole,
    request: SpatialRepresentationLoadRequest,
    signal?: AbortSignal,
    cacheArtifact = true
  ): Promise<SpatialRepresentationBuildResult<MeshBVHBackend>> {
    throwIfAborted(signal)
    const artifact = cacheArtifact
      ? await this.resolveArtifact(request)
      : await this.normalizeArtifact(
          request,
          createSourceReference(request.source, resolveFormat(request))
        )
    let backend: MeshBVHBackend
    try {
      throwIfAborted(signal)
      backend = await MeshBVHBackend.create(artifact)
    } finally {
      if (!cacheArtifact) artifact.geometry.dispose()
    }
    if (signal?.aborted) {
      backend.dispose()
      throwIfAborted(signal)
    }
    return {
      backend,
      format: artifact.source.format,
      bounds: artifact.bounds?.clone() ?? null,
      metadata: artifact.metadata,
      debug: createDebugSnapshot(role, request.debug),
      userData: Object.freeze({ ...(request.userData ?? {}) }),
    }
  }

  private resolveArtifact(
    request: SpatialRepresentationLoadRequest
  ): Promise<NormalizedSpatialArtifact> {
    const format = resolveFormat(request)
    const sourceRef = createSourceReference(request.source, format)
    sourceRef.key = JSON.stringify([sourceRef.key, format, request.artifact?.key ?? null])
    const cached = this.artifactCache.get(sourceRef.key)
    if (cached) {
      return cached
    }

    let pending: Promise<NormalizedSpatialArtifact>
    pending = this.importOrNormalizeArtifact(request, sourceRef).finally(() => {
      if (this.artifactCache.get(sourceRef.key) === pending) {
        this.artifactCache.delete(sourceRef.key)
      }
    })
    this.artifactCache.set(sourceRef.key, pending)
    return pending
  }

  private async importOrNormalizeArtifact(
    request: SpatialRepresentationLoadRequest,
    sourceRef: SpatialRepresentationSourceSnapshot
  ): Promise<NormalizedSpatialArtifact> {
    const exchange = request.artifact
    if (exchange) {
      try {
        const data = await exchange.read()
        if (data) {
          const restored = decodeSpatialArtifact(data, sourceRef)
          log.debug(`恢复空间工件: ${sourceRef.key}`)
          return restored
        }
      } catch (error) {
        log.warn(`空间工件导入失败，重新构建: ${String(error)}`)
      }
    }
    const artifact = await this.normalizeArtifact(request, sourceRef)
    if (exchange) {
      artifact.exportArtifact = (geometry, bvh) => {
        void Promise.resolve()
          .then(async () => {
            const data = encodeSpatialArtifact(artifact, geometry, bvh)
            await exchange.write(data)
          })
          .catch((error) => {
            log.warn(`空间工件导出失败: ${String(error)}`)
          })
      }
    }
    return artifact
  }

  private async normalizeArtifact(
    request: SpatialRepresentationLoadRequest,
    sourceRef: SpatialRepresentationSourceSnapshot
  ): Promise<NormalizedSpatialArtifact> {
    const { source } = request
    const withCredentials = this.withCredentials || request.withCredentials === true

    if (typeof source === 'string') {
      if (sourceRef.format === 'ply') {
        const geometry = request.telemetry
          ? await this.loadTelemetryPly(source, request.telemetry, withCredentials)
          : await this.createPlyLoader(withCredentials).loadAsync(source)
        return createArtifactFromGeometry(
          prepareGeometry(geometry, { requireIndexedFaces: true }),
          sourceRef
        )
      }

      if (sourceRef.format === 'glb' || sourceRef.format === 'gltf') {
        const gltf = await this.createGltfLoader(withCredentials).loadAsync(source)
        return createArtifactFromObject(gltf.scene, sourceRef)
      }

      throw new Error(`Unsupported spatial source format for "${source}"`)
    }

    if (source instanceof THREE.BufferGeometry) {
      return createArtifactFromGeometry(
        prepareGeometry(source, { requireIndexedFaces: false }),
        sourceRef
      )
    }

    if (source instanceof THREE.Object3D) {
      return createArtifactFromObject(source, sourceRef)
    }

    throw new Error('Unsupported spatial world source')
  }

  private createGltfLoader(withCredentials: boolean): GLTFLoader {
    const loader = new GLTFLoader(this.loadingManager)
    loader.withCredentials = withCredentials
    return loader
  }

  private createPlyLoader(withCredentials: boolean): PLYLoader {
    const loader = new PLYLoader(this.loadingManager)
    loader.withCredentials = withCredentials
    return loader
  }

  private async loadTelemetryPly(
    source: string,
    telemetry: NonNullable<SpatialRepresentationLoadRequest['telemetry']>,
    withCredentials: boolean
  ): Promise<THREE.BufferGeometry> {
    const attempt = beginGSplatResourceAttempt(telemetry, {
      resourceType: 'proxy-mesh',
      resourceIdentity: source,
      resourceUrl: source,
    })
    try {
      const response = await fetch(source, {
        credentials: withCredentials ? 'include' : 'same-origin',
      })
      attempt.markResponse(response)
      if (!response.ok) {
        throw new Error(`Proxy mesh request failed with status ${response.status}`)
      }
      const data = await response.arrayBuffer()
      attempt.complete(data.byteLength)
      return await measureGSplatLoadStage(
        telemetry,
        {
          resourceType: 'proxy-mesh',
          resourceIdentity: source,
          resourceUrl: source,
          stage: 'metadata-parse',
        },
        async () => this.createPlyLoader(withCredentials).parse(data)
      )
    } catch (error) {
      attempt.fail(error)
      throw error
    }
  }
}

function createArtifactFromGeometry(
  geometry: THREE.BufferGeometry,
  source: SpatialRepresentationSourceSnapshot
): NormalizedSpatialArtifact {
  const semantic = extractSemanticMetadata(geometry.userData)
  const meshMetadata = createMeshMetadataSnapshot({
    meshId: geometry.uuid,
    meshName: geometry.name || geometry.uuid,
    nodeId: geometry.uuid,
    nodeName: geometry.name || geometry.uuid,
    groupRanges: createGroupRanges(geometry),
    semantic,
  })
  const triangleMetadata = createTriangleMetadata(geometry, meshMetadata)
  const metadata: SpatialRepresentationMetadataSnapshot = {
    source,
    meshMetadata: [meshMetadata],
    semanticTags: semantic.tags,
  }

  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()

  return {
    source,
    geometry,
    bounds: geometry.boundingBox?.clone() ?? null,
    metadata,
    triangleMetadata,
  }
}

function createArtifactFromObject(
  root: THREE.Object3D,
  source: SpatialRepresentationSourceSnapshot
): NormalizedSpatialArtifact {
  root.updateMatrixWorld(true)
  const rootInverse = new THREE.Matrix4().copy(root.matrixWorld).invert()
  const geometries: THREE.BufferGeometry[] = []
  const meshMetadata: SpatialMeshMetadataSnapshot[] = []
  const triangleMetadata: SpatialHitMetadata[] = []
  const semanticTags = new Set<string>()

  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || !(object.geometry instanceof THREE.BufferGeometry)) {
      return
    }

    const prepared = prepareGeometry(object.geometry, {
      requireIndexedFaces: false,
    })
    const relativeMatrix = new THREE.Matrix4().copy(rootInverse).multiply(object.matrixWorld)
    prepared.applyMatrix4(relativeMatrix)
    prepared.computeBoundingBox()
    prepared.computeBoundingSphere()

    const semantic = extractSemanticMetadata(object.geometry.userData, object.userData)
    for (const tag of semantic.tags) {
      semanticTags.add(tag)
    }

    const snapshot = createMeshMetadataSnapshot({
      meshId: object.uuid,
      meshName: object.name || object.uuid,
      nodeId: object.uuid,
      nodeName: object.name || object.uuid,
      groupRanges: createGroupRanges(prepared, object.material),
      semantic,
    })

    geometries.push(prepared)
    meshMetadata.push(snapshot)
    triangleMetadata.push(...createTriangleMetadata(prepared, snapshot))
  })

  if (geometries.length === 0) {
    throw new Error('Spatial world source must contain triangle positions')
  }

  const merged = geometries.length === 1 ? geometries[0] : mergeGeometries(geometries, true)
  if (!merged) {
    throw new Error('Failed to merge normalized spatial geometries')
  }

  for (const geometry of geometries) {
    if (geometry !== merged) {
      geometry.dispose()
    }
  }

  merged.computeBoundingBox()
  merged.computeBoundingSphere()

  return {
    source,
    geometry: merged,
    bounds: merged.boundingBox?.clone() ?? null,
    metadata: {
      source,
      meshMetadata,
      semanticTags: Array.from(semanticTags),
    },
    triangleMetadata,
  }
}

function createDebugSnapshot(
  role: SpatialRepresentationRole,
  debug?: SpatialRepresentationDebugOptions
): SpatialRepresentationDebugSnapshot {
  return {
    prototypeName: `${role}-prototype`,
    meshVisible: debug?.meshVisible ?? false,
    boundsVisible: debug?.boundsVisible ?? false,
    boundsDepth: debug?.boundsDepth ?? 10,
  }
}

function prepareGeometry(
  source: THREE.BufferGeometry,
  options: { requireIndexedFaces: boolean }
): THREE.BufferGeometry {
  const position = source.getAttribute('position')
  if (!position || position.count < 3) {
    throw new Error('Spatial world source must contain triangle positions')
  }

  if (options.requireIndexedFaces && !source.index) {
    throw new Error('PLY spatial source must contain triangle faces')
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', position.clone())

  if (source.index) {
    geometry.setIndex(source.index.clone())
  }

  const normal = source.getAttribute('normal')
  if (normal) {
    geometry.setAttribute('normal', normal.clone())
  } else {
    geometry.computeVertexNormals()
  }

  for (const group of source.groups) {
    geometry.addGroup(group.start, group.count, group.materialIndex)
  }

  const indexCount = geometry.index?.count
  const vertexCount = geometry.getAttribute('position').count
  const primitiveCount = indexCount ?? vertexCount
  if (primitiveCount < 3 || primitiveCount % 3 !== 0) {
    throw new Error('Spatial world source must resolve to triangle primitives')
  }

  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()
  geometry.name = source.name
  geometry.userData = { ...(source.userData ?? {}) }
  return geometry
}

function createSourceReference(
  source: SpatialWorldSource,
  format: SpatialSourceFormat | null
): SpatialRepresentationSourceSnapshot {
  if (typeof source === 'string') {
    return {
      key: `url:${source}`,
      kind: 'url',
      format,
    }
  }

  if (source instanceof THREE.BufferGeometry) {
    return {
      key: `buffer-geometry:${source.uuid}`,
      kind: 'buffer-geometry',
      format,
    }
  }

  return {
    key: `object3D:${source.uuid}`,
    kind: 'object3D',
    format,
  }
}

function resolveFormat(request: SpatialRepresentationLoadRequest): SpatialSourceFormat | null {
  if (request.format) {
    return request.format
  }

  if (typeof request.source !== 'string') {
    return null
  }

  const normalized = request.source.toLowerCase()
  if (normalized.endsWith('.glb')) {
    return 'glb'
  }
  if (normalized.endsWith('.gltf')) {
    return 'gltf'
  }
  if (normalized.endsWith('.ply')) {
    return 'ply'
  }

  return null
}

function createGroupRanges(
  geometry: THREE.BufferGeometry,
  material?: THREE.Material | ReadonlyArray<THREE.Material>
): SpatialGroupRangeSnapshot[] {
  const groups = geometry.groups.length
    ? geometry.groups
    : [
        {
          start: 0,
          count: geometry.index?.count ?? geometry.getAttribute('position').count,
          materialIndex: 0,
        },
      ]

  return groups.map((group, index) => ({
    groupIndex: index,
    start: group.start,
    count: group.count,
    materialIndex: group.materialIndex,
    materialName: getMaterialName(material, group.materialIndex),
  }))
}

function createMeshMetadataSnapshot(input: {
  meshId: string
  meshName: string
  nodeId: string
  nodeName: string
  groupRanges: ReadonlyArray<SpatialGroupRangeSnapshot>
  semantic: SpatialSemanticMetadata
}): SpatialMeshMetadataSnapshot {
  return {
    meshId: input.meshId,
    meshName: input.meshName,
    nodeId: input.nodeId,
    nodeName: input.nodeName,
    groupRanges: input.groupRanges,
    semantic: input.semantic,
  }
}

function createTriangleMetadata(
  geometry: THREE.BufferGeometry,
  mesh: SpatialMeshMetadataSnapshot
): SpatialHitMetadata[] {
  const triangleCount = (geometry.index?.count ?? geometry.getAttribute('position').count) / 3
  const metadata = Array.from({ length: triangleCount }, () =>
    createHitMetadata(mesh, undefined, undefined)
  )

  if (!geometry.groups.length) {
    return metadata
  }

  for (const group of mesh.groupRanges) {
    const startTriangle = Math.floor(group.start / 3)
    const triangleSpan = Math.ceil(group.count / 3)
    for (
      let faceIndex = startTriangle;
      faceIndex < Math.min(triangleCount, startTriangle + triangleSpan);
      faceIndex += 1
    ) {
      metadata[faceIndex] = createHitMetadata(mesh, group.materialIndex, group.materialName)
    }
  }

  return metadata
}

function createHitMetadata(
  mesh: SpatialMeshMetadataSnapshot,
  materialIndex?: number,
  materialName?: string | null
): SpatialHitMetadata {
  return {
    meshId: mesh.meshId,
    meshName: mesh.meshName,
    nodeId: mesh.nodeId,
    nodeName: mesh.nodeName,
    materialIndex,
    materialName,
    semantic: mesh.semantic,
  }
}

function extractSemanticMetadata(
  ...sources: Array<Record<string, unknown> | undefined>
): SpatialSemanticMetadata {
  const tags = new Set<string>()
  const mergedUserData: Record<string, unknown> = {}
  let walkable: boolean | undefined
  let blocked: boolean | undefined

  for (const source of sources) {
    if (!source) {
      continue
    }

    Object.assign(mergedUserData, source)
    collectTags(source, tags)

    walkable = pickBoolean(
      walkable,
      source.walkable,
      (source.navigation as { walkable?: boolean } | undefined)?.walkable,
      (source.semantic as { walkable?: boolean } | undefined)?.walkable
    )
    blocked = pickBoolean(
      blocked,
      source.blocked,
      (source.navigation as { blocked?: boolean } | undefined)?.blocked,
      (source.semantic as { blocked?: boolean } | undefined)?.blocked
    )
  }

  return {
    tags: Array.from(tags),
    walkable,
    blocked,
    userData: Object.freeze(mergedUserData),
  }
}

function collectTags(source: Record<string, unknown>, target: Set<string>): void {
  const candidates = [
    source.tags,
    source.semanticTags,
    source.spatialTags,
    (source.navigation as { tags?: unknown } | undefined)?.tags,
    (source.semantic as { tags?: unknown } | undefined)?.tags,
  ]

  for (const candidate of candidates) {
    for (const tag of normalizeTags(candidate)) {
      target.add(tag)
    }
  }
}

function normalizeTags(candidate: unknown): string[] {
  if (Array.isArray(candidate)) {
    return candidate.filter((value): value is string => typeof value === 'string')
  }
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

function pickBoolean(current: boolean | undefined, ...candidates: unknown[]): boolean | undefined {
  if (typeof current === 'boolean') {
    return current
  }

  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') {
      return candidate
    }
  }

  return undefined
}

function getMaterialName(
  material: THREE.Material | ReadonlyArray<THREE.Material> | undefined,
  materialIndex?: number
): string | null {
  if (!material) {
    return null
  }

  if (Array.isArray(material)) {
    const entry = materialIndex !== undefined ? material[materialIndex] : material[0]
    return entry?.name || null
  }

  const singleMaterial = material as THREE.Material
  return singleMaterial.name || null
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) {
    return
  }

  const error = new Error('Spatial world build aborted')
  error.name = 'AbortError'
  throw error
}
